/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { memo } from "react";
import { cx } from "@panda/css";
import { useTabsContext } from "./tabs.context";

export interface CosmosTabsPanelProps {
	/** The numeric index that this content panel corresponds to */
	index: number;
	/** Content to display when this tab is active */
	children: React.ReactNode;
	/** Custom CSS class name */
	className?: string;
}

/**
 * Content panel that displays when its corresponding tab button is active. Must have a numeric index that matches a Tabs.Button.
 */
export const CosmosTabsPanel = memo(
	({ index, children, className }: CosmosTabsPanelProps): React.JSX.Element | null => {
		const { activeIndex, hasTabsList } = useTabsContext();

		const isSelected = activeIndex === index;

		// If not selected, don't render
		if (!isSelected) {
			return null;
		}

		// Only apply tab ARIA roles when CosmosTabsList is present
		// This prevents invalid ARIA when there's only one content panel without tabs
		const role = hasTabsList ? "tabpanel" : undefined;

		// Generate stable IDs for ARIA relationships
		const tabId = `tab-${index}`;
		const panelId = `panel-${index}`;
		const ariaLabelledBy = hasTabsList ? tabId : undefined;

		return (
			<div
				id={panelId}
				className={cx("cosmos-tabs-content", className)}
				role={role}
				aria-labelledby={ariaLabelledBy}
				style={{ height: "100%" }}
			>
				{children}
			</div>
		);
	},
);

CosmosTabsPanel.displayName = "CosmosTabs.Panel";
